import { memo, useEffect, useMemo, useState, Fragment } from "react";
import PropTypes from "prop-types";
import { isEmpty, omit } from "lodash";
import { useField } from "formik";
import classNames from "classnames";
import { messagePropType } from "app/utils/propTypes";
import {
	FloatingPortal,
	offset,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useTransitionStyles,
} from "@floating-ui/react";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import AdvancedSelectFormik from "app/pages/.shared/form/AdvancedSelectFormik/AdvancedSelectFormik";
import LoaderBar from "app/pages/.shared/LoaderBar/LoaderBar";
import "./FloatingAdvancedSelectFormik.scss";

const FloatingAdvancedSelectFormik = props => {
	const {
		id,
		label,
		className,
		isRequired,
		getOptionLabel = () => {},
		floatingConfig = {},
		isSearchable = true,
		selectRef = {},
		loading,
		usePortalForMenu, // useful for search form above an overlay in sdp listing
		name,
	} = props;

	const [field, meta] = useField(name);

	const [showFloatingModal, setFloatingModal] = useState(false);

	const isTouched = (meta.touched && !meta.error) || field.value;

	const { x, y, refs, strategy, context } = useFloating({
		placement: floatingConfig.placement,
		strategy: "absolute",
		open: showFloatingModal,
		onOpenChange: setFloatingModal,
		middleware: [offset(floatingConfig.offset), shift()],
	});
	const dismiss = useDismiss(context, { ancestorScroll: false });
	const click = useClick(context, {
		event: "mousedown",
		toggle: false,
		keyboardHandlers: false,
	});
	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);
	const { styles: transitionStyles, isMounted } = useTransitionStyles(context, {
		duration: 250,
		initial: {
			opacity: 0,
			transform: "scale(0.95)",
		},
		open: {
			opacity: 1,
			transform: "scale(1)",
		},
		close: {
			opacity: 0,
			transform: "scale(0.95)",
		},
	});

	const containerClassName = useMemo(
		() =>
			classNames("floating-advanced-select-container", className, {
				"floating-advanced-select-container--required": isRequired,
				"floating-advanced-select-container--touched": isTouched,
				"floating-advanced-select-container--error ": meta.touched && meta.error,
				"floating-advanced-select-container--focus": showFloatingModal,
			}),
		[className, isRequired, isTouched, meta.touched, meta.error]
	);

	useEffect(() => {
		if (!isEmpty(field.value)) {
			setFloatingModal(false);
		}
	}, [field.value]);

	const PortalOrFragment = usePortalForMenu ? FloatingPortal : Fragment;

	return (
		<div
			className={containerClassName}
			ref={refs.setReference}
			{...getReferenceProps({})}
			data-testid={props["data-testid"]}
		>
			{!loading ? (
				<div className="floating-advanced-select__label-container" ref={selectRef}>
					<label htmlFor={id} className="floating-advanced-select__label">
						<Typography
							variant={
								field.value
									? TYPOGRAPHY_VARIANTS.XSMALL
									: TYPOGRAPHY_VARIANTS.REGULAR
							}
							data-testid={`${props["data-testid"]}-label`}
						>
							{label}
						</Typography>
						{field.value && (
							<Typography
								variant={TYPOGRAPHY_VARIANTS.REGULAR}
								className="floating-advanced-select__value"
							>
								{getOptionLabel(field.value)}
							</Typography>
						)}
					</label>
				</div>
			) : (
				<div className="floating-advanced-select__loader">
					<LoaderBar height={10} width={"80%"} />
					<LoaderBar height={10} />
				</div>
			)}

			{showFloatingModal && isMounted && (
				<PortalOrFragment>
					<div
						className={classNames("floating-advanced-select__modal", {
							"floating-advanced-select__modal--touched": isTouched,
						})}
						ref={refs.setFloating}
						{...getFloatingProps({
							style: {
								position: strategy,
								left: x,
								top: y,
								zIndex: 10,
								...transitionStyles,
							},
						})}
					>
						<AdvancedSelectFormik
							menuIsOpen={showFloatingModal}
							autoFocus={isSearchable}
							isClearable={isSearchable}
							label={label}
							classNamePrefix="advanced-select__select-sdp"
							menuPortalTarget={null}
							{...omit(props, ["selectRef"])} // selectRef is used on the label container element.
							onChange={value => {
								props.onChange(value);
								if (Boolean(value) && !isEmpty(value)) {
									setFloatingModal(false);
								}
							}}
						/>
					</div>
				</PortalOrFragment>
			)}
		</div>
	);
};

FloatingAdvancedSelectFormik.propTypes = {
	id: PropTypes.string,
	label: messagePropType,
	getOptionLabel: PropTypes.func,
	getOptionValue: PropTypes.func,
	onChange: PropTypes.func,
	options: PropTypes.array,
	onFocus: PropTypes.func,
	onMenuClose: PropTypes.func,
	isRequired: PropTypes.bool,
	captureMenuScroll: PropTypes.bool,
	menuShouldBlockScroll: PropTypes.bool,
	loading: PropTypes.bool,
	usePortalForMenu: PropTypes.bool,
	name: PropTypes.string,
	floatingConfig: PropTypes.object,
	isSearchable: PropTypes.bool,
	selectRef: PropTypes.object,
	placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	"data-testid": PropTypes.string,
};

export default memo(FloatingAdvancedSelectFormik);
